import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	activateUser,
	deactivateUser,
	removeUser,
	userUpdate,
} from '../../api';
import {
	createToken,
	deleteToken,
	getRoles,
	getTeam,
	getTokens,
	resendToken,
} from '../../api/company';

import { getUserFullName } from '../../components/helpers/functions';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import { AvatarGenerated } from '../../components/Avatar';
import Button from '../../components/Button';
import { DataTable, FilterDropdown, StackableModal } from '../../components/nextgen';
import EllipsisDropdown, { EllipsisDropdownItem } from '../../components/EllipsisDropdown';

import useAsync from '../../hooks/useAsync';
import useLocal from '../../hooks/useLocal';
import useModal from '../../hooks/useModal';
import {
	deletePowerUser,
	getAllPowerUsers,
	toggleNotifications,
} from '../../api/power-users';
import usePrevious from '../../hooks/usePrevious';

const DEFAULT_FORM = {
	role_id: 'da13e70e-6713-4ec6-bfa6-018c5e2c4c04',
};

const Team = () => {
	const navigate = useNavigate();

	const { tab } = useParams();

	const { open: addPowerUserOpen, setOpen: onOpenAddPowerUser } =
		useModal('addPowerUser');

	const [form, setForm] = useState(DEFAULT_FORM);
	const [isChangeRoleModalOpen, setChangeRoleModalOpen] = useState(false);

	const { get } = useLocal();
	const localUser = get('user');

	const [isCreateModalOpen, setCreateModalOpen] = useState(false);

	useEffect(() => {
		if (tab === 'new') {
			setCreateModalOpen(true);
		}
	}, [tab]);

	const { value: team, execute: refreshTeam } = useAsync(getTeam);
	const { value: powerUsers, execute: refreshPowerUsers } =
		useAsync(getAllPowerUsers);
	const { value: tokens, execute: refreshTokens } = useAsync(getTokens);
	const { value: roles, execute: refreshRoles } = useAsync(getRoles);
	const { status: creatingToken, execute: onCreateToken } = useAsync(
		createToken,
		{
			immediate: false,
			showNotification: {
				success: 'A user has been create.  Email sent.',
				error: 'raw',
			},
		},
	);
	const { status: activatingUser, execute: onActivateUser } = useAsync(
		activateUser,
		{
			immediate: false,
			showNotification: { success: 'User activated', error: 'raw' },
		},
	);
	const { status: deactivatingUser, execute: onDeactivateUser } = useAsync(
		deactivateUser,
		{
			immediate: false,
			showNotification: { success: 'User deactivated', error: 'raw' },
		},
	);

	const { execute: onDeletePowerUser } = useAsync(deletePowerUser, {
		immediate: false,
	});

	const { execute: onUpdatePowerUser } = useAsync(toggleNotifications, {
		immediate: false,
		showNotification: {
			success: 'Notifications updated',
			error: 'Uh oh...something went wrong',
		},
	});

	const { execute: onResendToken } = useAsync(resendToken, {
		immediate: false,
		showNotification: { success: 'Email sent', error: 'raw' },
	});

	const { execute: onDeleteToken } = useAsync(deleteToken, {
		immediate: false,
		showNotification: { success: 'Invitation deleted', error: 'raw' },
	});

	const { execute: onRemoveUser } = useAsync(removeUser, {
		immediate: false,
		showNotification: { success: 'User removed' },
	});

	const { execute: onUpdateUser, status: updatingRole } = useAsync(userUpdate, {
		immediate: false,
		showNotification: { success: 'User updated' },
	});

	const prevAddPowerUserOpen = usePrevious(addPowerUserOpen);
	useEffect(() => {
		if (!addPowerUserOpen && prevAddPowerUserOpen) {
			refreshPowerUsers();
		}
	}, [addPowerUserOpen]);

	useEffect(() => {
		if (!isCreateModalOpen || !isChangeRoleModalOpen) {
			setForm(DEFAULT_FORM);
			refreshTeam(null, null, { silent: true });
			refreshTokens(null, null, { silent: true });
		}
	}, [isCreateModalOpen, isChangeRoleModalOpen]);

	const activeTeam = team?.rows?.filter((u) => u.active);
	const inactiveTeam = team?.rows?.filter((u) => !u.active);

	// Category filter state derived from URL tab param
	const currentCategory = tab || 'active';

	const categoryOptions = [
		{
			value: 'active',
			label: `Active (${activeTeam?.length || 0})`,
		},
		{
			value: 'pending',
			label: `Pending (${tokens?.rows?.length || 0})`,
		},
		{
			value: 'inactive',
			label: `Inactive (${inactiveTeam?.length || 0})`,
		},
		{
			value: 'power-users',
			label: `Power Users (${powerUsers?.length || 0})`,
		},
	];

	const handleCategoryChange = (value) => {
		if (value === 'active') {
			navigate('/manage/team');
		} else {
			navigate(`/manage/team/${value}`);
		}
	};

	// Get data source based on category
	const source = useMemo(() => {
		if (!tab || tab === 'active') {
			return activeTeam || [];
		}
		if (tab === 'pending') {
			return tokens?.rows || [];
		}
		if (tab === 'inactive') {
			return inactiveTeam || [];
		}
		if (tab === 'power-users') {
			return powerUsers || [];
		}
		return [];
	}, [tab, activeTeam, inactiveTeam, tokens, powerUsers]);

	// Build columns based on current tab
	const columns = useMemo(() => {
		const baseColumns = [
			{
				key: 'avatar',
				header: '',
				sortable: false,
				width: '50px',
				render: (row) => (
					<AvatarGenerated
						size="xs"
						id={getUserFullName({
							firstName: row?.firstName,
							lastName: row?.lastName,
							...row,
						})}
					/>
				),
			},
			{
				key: 'email',
				header: 'Email',
				render: (row) => row?.email || '',
			},
		];

		// Add name column for non-pending tabs
		if (tab !== 'pending') {
			baseColumns.push({
				key: 'name',
				header: 'Name',
				sortable: false,
				render: (row) => {
					return row.firstName || row.lastName || row.first_name || row.last_name
						? getUserFullName(row)
						: 'Not provided';
				},
			});
		}

		// Add role column for non-power-users tabs
		if (tab !== 'power-users') {
			baseColumns.push({
				key: 'role',
				header: 'Role',
				render: (row) => row?.role?.displayName || '',
			});
		}

		// Add pending-specific columns
		if (tab === 'pending') {
			baseColumns.push(
				{
					key: 'token',
					header: 'Token',
					render: (row) => row?.token || '',
				},
				{
					key: 'expires',
					header: 'Expires',
					render: (row) => row?.expires || '',
				},
			);
		}

		return baseColumns;
	}, [tab]);

	// Row actions based on current tab
	const renderRowActions = (row) => {
		if (!tab || tab === 'active') {
			if (row.id === localUser.id) return null;
			return (
				<EllipsisDropdown>
					<EllipsisDropdownItem
						onClick={() =>
							setChangeRoleModalOpen({
								id: row?.id,
								role_id: row?.role_id,
							})
						}
					>
						Change Role
					</EllipsisDropdownItem>
					<EllipsisDropdownItem
						danger
						onClick={() => {
							if (window.confirm('Are you sure you want to deactivate this user?')) {
								onDeactivateUser({ id: row?.id }, () =>
									refreshTeam(null, null, { silent: true }),
								);
							}
						}}
					>
						Deactivate
					</EllipsisDropdownItem>
				</EllipsisDropdown>
			);
		}

		if (tab === 'pending') {
			return (
				<EllipsisDropdown>
					<EllipsisDropdownItem onClick={() => onResendToken({ id: row?.id })}>
						Resend Invite
					</EllipsisDropdownItem>
					<EllipsisDropdownItem
						danger
						onClick={() => {
							if (window.confirm('Are you sure you want to delete this invite?')) {
								onDeleteToken({ id: row?.id }, () =>
									refreshTokens(null, null, { silent: true }),
								);
							}
						}}
					>
						Delete Invite
					</EllipsisDropdownItem>
				</EllipsisDropdown>
			);
		}

		if (tab === 'inactive') {
			return (
				<EllipsisDropdown>
					<EllipsisDropdownItem
						onClick={() =>
							onActivateUser({ id: row?.id }, () =>
								refreshTeam(null, null, { silent: true }),
							)
						}
					>
						Reactivate
					</EllipsisDropdownItem>
					<EllipsisDropdownItem
						danger
						onClick={() => {
							if (window.confirm('Are you sure you want to delete this user?')) {
								onRemoveUser({ id: row?.id }, () =>
									refreshTeam(null, null, { silent: true }),
								);
							}
						}}
					>
						Delete User
					</EllipsisDropdownItem>
				</EllipsisDropdown>
			);
		}

		if (tab === 'power-users') {
			return (
				<EllipsisDropdown>
					<EllipsisDropdownItem
						danger
						onClick={() => {
							if (window.confirm('Are you sure you want to delete this power user?')) {
								onDeletePowerUser(row?.id, () =>
									refreshPowerUsers(null, null, { silent: true }),
								);
							}
						}}
					>
						Delete Power User
					</EllipsisDropdownItem>
				</EllipsisDropdown>
			);
		}

		return null;
	};

	const { updatedRoleId, ...restForm } = form;

	const getPageTitle = () => {
		if (!tab || tab === 'active') return 'Active Accounts';
		if (tab === 'pending') return 'Pending';
		if (tab === 'inactive') return 'Inactive';
		if (tab === 'power-users') return 'Power Users';
		return 'Team';
	};

	return (
		<>
			<MainContent maxWidth="max-w-7xl">
				<PageHeading
					actions={[
						{
							name: 'Add Power User',
							type: 'secondary',
							onClick: () => onOpenAddPowerUser(),
						},
						{
							name: 'Add User Account',
							onClick: () => navigate('/manage/team/new'),
						},
					]}
					divider={false}
				>
					Manage Team
				</PageHeading>

				<div className="mb-4">
					<div className="flex items-center gap-3 mb-4">
						<FilterDropdown
							label="Category"
							placeholder="Select category"
							value={currentCategory}
							options={categoryOptions}
							onChange={handleCategoryChange}
							clearable={false}
						/>
					</div>

					<DataTable
						columns={columns}
						data={source}
						keyField="id"
						sortable={true}
						defaultSort={{ key: 'email', direction: 'asc' }}
						rowActions={renderRowActions}
						onRowClick={
							tab === 'power-users'
								? (row) => navigate(`/manage/team/power-user/${row.id}`)
								: undefined
						}
						emptyTitle={`No ${getPageTitle().toLowerCase()} found`}
						emptyDescription="Users will appear here once added"
					/>
				</div>
			</MainContent>

			<StackableModal
				title="Send invite"
				open={isCreateModalOpen}
				onClose={() => {
					setCreateModalOpen(false);
					navigate('/manage/team');
				}}
				size="md"
			>
				<div>
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Email <span className="text-red-500">*</span>
						</label>
						<input
							type="email"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							onChange={(e) => setForm({ ...form, email: e.target.value })}
							value={form?.email || ''}
						/>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Role <span className="text-red-500">*</span>
						</label>
						<select
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							onChange={(e) => setForm({ ...form, role_id: e.target.value })}
							value={form?.role_id || ''}
						>
							{roles?.map((role) => (
								<option key={role.id} value={role.id}>
									{role.display_name}
								</option>
							))}
						</select>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							First Name <span className="text-gray-400 text-xs">(optional)</span>
						</label>
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							onChange={(e) => setForm({ ...form, first_name: e.target.value })}
							value={form?.first_name || ''}
						/>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Last Name <span className="text-gray-400 text-xs">(optional)</span>
						</label>
						<input
							type="text"
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							onChange={(e) => setForm({ ...form, last_name: e.target.value })}
							value={form?.last_name || ''}
						/>
					</div>

					<div className="flex justify-end">
						<Button
							disabled={
								!['email', 'role_id'].every((a) =>
									Object.keys(restForm).includes(a),
								)
							}
							onClick={() =>
								onCreateToken(restForm, () => {
									setCreateModalOpen(false);
									navigate('/manage/team/pending');
								})
							}
						>
							{creatingToken === 'pending'
								? 'Sending invite...'
								: 'Send Invite'}
						</Button>
					</div>
				</div>
			</StackableModal>

			<StackableModal
				open={!!isChangeRoleModalOpen}
				onClose={() => setChangeRoleModalOpen(false)}
				title="Change user role"
				size="sm"
			>
				<div>
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1">
							Role
						</label>
						<select
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
							onChange={(e) => setForm({ ...form, updatedRoleId: e.target.value })}
							value={updatedRoleId || isChangeRoleModalOpen?.role_id || ''}
						>
							{roles?.map((role) => (
								<option key={role.id} value={role.id}>
									{role.display_name}
								</option>
							))}
						</select>
					</div>

					<div className="flex justify-end">
						<Button
							disabled={!form?.updatedRoleId}
							onClick={() =>
								onUpdateUser(
									{ id: isChangeRoleModalOpen?.id, role_id: form?.updatedRoleId },
									() => {
										setChangeRoleModalOpen(false);
										navigate('/manage/team');
									},
								)
							}
						>
							{updatingRole === 'pending' ? 'Updating...' : 'Update'}
						</Button>
					</div>
				</div>
			</StackableModal>
		</>
	);
};

export default Team;
